/**
 * LexiQuest — root application component.
 *
 * View router: landing → solo | lobby → multiplayer.
 * Solo mode uses useGame (HTTP REST). Multiplayer uses useMultiplayer (WebSocket).
 */

import { useState, useEffect } from 'react';
import useMultiplayer from './hooks/useMultiplayer';
import LandingPage from './components/LandingPage';
import SoloGame from './components/SoloGame';
import Lobby from './components/Lobby';
import MultiplayerGame from './components/MultiplayerGame';
import SEO from './seo/SEO';
import './styles/App.css';
import './styles/Multiplayer.css';

export default function App() {
  const [view, setView] = useState('landing'); // "landing" | "solo" | "lobby" | "multiplayer"

  const multiplayer = useMultiplayer();

  // Sync view with multiplayer room status
  useEffect(() => {
    if (multiplayer.roomStatus === 'lobby' && view !== 'lobby') {
      setView('lobby');
    }
    if (multiplayer.roomStatus === 'playing' && view !== 'multiplayer') {
      setView('multiplayer');
    }
    // Room disbanded or left — return to landing (unless in solo)
    if (multiplayer.roomStatus === null && (view === 'lobby' || view === 'multiplayer')) {
      setView('landing');
    }
  }, [multiplayer.roomStatus, view]);

  switch (view) {
    case 'landing':
      return (
        <>
          <SEO view="landing" />
          <LandingPage
            onSoloGame={() => setView('solo')}
            onCreateRoom={(name) => multiplayer.createRoom(name)}
            onJoinRoom={(code, name) => multiplayer.joinRoom(code, name)}
            error={multiplayer.error}
          />
        </>
      );

    case 'solo':
      return (
        <>
          <SEO view="solo" />
          <SoloGame
            onBack={() => setView('landing')}
          />
        </>
      );

    case 'lobby':
      return (
        <>
          <SEO view="lobby" />
          <Lobby
            roomCode={multiplayer.roomCode}
            players={multiplayer.players}
            isHost={multiplayer.isHost}
            difficulty={multiplayer.difficulty}
            previewWords={multiplayer.previewWords}
            onSetDifficulty={(d) => multiplayer.setRoomDifficulty(d)}
            onRerollWords={() => multiplayer.rerollWords()}
            onStartGame={() => multiplayer.startGame()}
            onLeaveRoom={() => multiplayer.leaveRoom()}
          />
        </>
      );

    case 'multiplayer':
      return (
        <>
          <SEO view="multiplayer" />
          <MultiplayerGame multiplayer={multiplayer} />
        </>
      );

    default:
      return null;
  }
}
